import { Controller } from "@hotwired/stimulus"

// Polls Admin::DashboardsController#page_view_series for a fresh render of the traffic chart, and
// lets the range buttons above it switch which window (and therefore which poll cadence; see
// PAGE_VIEW_RANGES) is showing. The server always re-renders the whole chart-or-empty-state
// fragment (see _page_view_chart_content.html.erb); this controller never draws anything itself,
// it only decides when to ask for a fresh copy and swaps it in wholesale, so there's exactly one
// place (the server-rendered SVG partial) that knows how to turn page-view counts into a chart.
export default class extends Controller {
  static values = { url: String, range: String, pollIntervals: Object }
  static targets = ["content", "rangeButton"]

  connect() {
    this.schedulePoll()
  }

  disconnect() {
    this.stopPoll()
  }

  selectRange(event) {
    const range = event.params.range
    if (range === this.rangeValue) return

    this.rangeValue = range
    this.rangeButtonTargets.forEach((button) => {
      const active = button.dataset.liveChartRangeParam === range
      button.classList.toggle("btn-primary", active)
      button.classList.toggle("btn-outline-secondary", !active)
    })
    this.refresh()
    this.schedulePoll()
  }

  schedulePoll() {
    this.stopPoll()
    const interval = this.pollIntervalsValue[this.rangeValue]
    if (!interval) return
    this.timer = setInterval(() => this.refresh(), interval)
  }

  stopPoll() {
    if (this.timer) clearInterval(this.timer)
  }

  // A poll the tab can't currently see is a production database query spent on a chart nobody's
  // looking at: skipped rather than fired, and picked back up by the next scheduled tick once
  // the tab is visible again. A failed request (the poll landing during a deploy, a network blip)
  // is swallowed the same way: this is a background refresh of an already-rendered chart, not
  // something a visitor is waiting on, so it's not worth surfacing as an error; the next poll
  // just tries again.
  async refresh() {
    if (document.hidden) return

    try {
      const response = await fetch(`${this.urlValue}?range=${this.rangeValue}`, { headers: { Accept: "text/html" } })
      if (response.ok) this.contentTarget.innerHTML = await response.text()
    } catch {
      // See comment above: a dropped poll isn't worth doing anything about.
    }
  }
}
